/* eslint-disable no-restricted-syntax */
import { ICurrentEmployee } from 'store/types';
import { EmployeeRole, EmployeeType, IEmployer, PermissionType, SiteRole } from 'types';
import { sortAlphaNumerically } from './arrays';

/**
 * finds the first employer in the array where the type is ADMIN
 * or the first site where the role is ADMIN or MANAGER.
 *
 * @param employers - an array of IEmployer objects
 * @returns - a ICurrentEmployee object.
 */
export const findFirstManagementEmployer = (employers: IEmployer[]): ICurrentEmployee => {
  // Sort the employers by name. Copy first — the caller's array is not ours to reorder.
  const sortedEmployers = [...employers].sort(sortAlphaNumerically('name'));

  // Find the first employer where the user has organization admin permissions.
  const employee = sortedEmployers.find((employer) => employer.type === EmployeeType.ADMIN);
  if (employee) return { organizationId: employee.id };

  // When no organization admin permissions are found
  // Find the first employer with site-level management permissions.
  for (const employer of sortedEmployers) {
    const sortedSites = [...(employer.sites || [])].sort(sortAlphaNumerically('name'));
    for (const site of sortedSites) {
      if (site.role !== SiteRole.EMPLOYEE) return { organizationId: employer.id, siteId: site.id, id: site.employee?.id };
    }
  }

  return undefined;
};

/**
 * takes current EmployeeType and SiteRole and returns appropriate EmployeeRole
 *
 * @param currentType - an EmployeeType
 * @param currentRole - a SiteRole
 * @returns - an EmployeeRole
 */
export const transformCurrentEmployeeRole = (currentType: EmployeeType, currentRole: SiteRole): EmployeeRole => {
  const transformedRole = currentType === EmployeeType.USER && currentRole === SiteRole.ADMIN ? EmployeeRole.SITE_ADMIN : currentRole;
  return transformedRole as EmployeeRole;
};

/**
 * Checks if a given permission type exists within a list of permissions.
 *
 * @param {PermissionType[]} permissions - An array of permissions to check against.
 *                                         Can be `undefined` or `null`, in which case the function returns `false`.
 * @param {PermissionType} type - The specific permission type to search for in the `permissions` array.
 *
 * @returns {boolean} - Returns `true` if the `type` permission is found in the `permissions` array, otherwise `false`.
 *                      If `permissions` is `undefined` or `null`, returns `false`.
 */
export const hasSitePermission = (permissions: PermissionType[], type: PermissionType): boolean => permissions?.some((permission) => permission === type);
